/**
 * Google Gemini Provider Implementation
 * 
 * This module implements the AIProvider interface for Google's Gemini API.
 * Supports Gemini Pro, Gemini Flash, and other Gemini models.
 */

import { GoogleGenAI } from '@google/genai';
import {
  AIProvider,
  AIProviderConfig,
  ChatMessage,
  ChatCompletionOptions,
  ChatCompletionResponse,
} from './types';

export class GoogleProvider implements AIProvider {
  readonly providerType = 'google' as const;
  private client: GoogleGenAI;
  private config: AIProviderConfig;

  constructor(apiKey: string, config: AIProviderConfig) {
    this.client = new GoogleGenAI({ apiKey });
    this.config = config;
  }

  async chat(messages: ChatMessage[], options?: ChatCompletionOptions): Promise<ChatCompletionResponse> {
    // Separate system message from conversation messages
    const systemMessage = messages.find(msg => msg.role === 'system');
    const conversationMessages = messages.filter(msg => msg.role !== 'system');

    // Convert messages to Gemini format
    const contents = conversationMessages.map(msg => ({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: [{ text: msg.content }],
    }));

    const response = await this.client.models.generateContent({
      model: this.config.model,
      contents,
      config: {
        temperature: this.config.temperature,
        maxOutputTokens: this.config.maxTokens,
        ...(systemMessage && {
          systemInstruction: systemMessage.content,
        }),
        ...(options?.jsonMode && {
          responseMimeType: 'application/json',
        }),
      },
    });

    const text = response.text || '';
    const finishReason = response.candidates?.[0]?.finishReason;
    const usageMetadata = response.usageMetadata;

    return {
      content: text,
      finishReason: finishReason || undefined,
      usage: usageMetadata ? {
        promptTokens: usageMetadata.promptTokenCount || 0,
        completionTokens: usageMetadata.candidatesTokenCount || 0,
        totalTokens: usageMetadata.totalTokenCount || 0,
      } : undefined,
    };
  }
}

